// The dynamic keyboard as the compose page mounts it, unchanged from its
// package and wrapped in the host's two duties: the suggest call that knows
// the story, and the gate that lets only a dictionary word or an offered chip
// into the composition. The keyboard hands over letters nothing could check
// as "unchecked" rather than leaving the child stuck; here they meet the
// gate like any other word and become the rescue, so no misspelling is
// committed. When the typed letters leave the word list the rescue appears
// in place, with the best chip so far, "say it instead" and, on a word
// already in the story, "save it for a grown-up". No word is ever rejected
// out loud; a refused commit simply becomes the rescue.
import { fetchToken } from "@bindery/auth-consumer/browser";
import {
  DynamicKeyboard,
  type KeyboardModel,
  loadKeyboardModel,
  type SuggestWord,
} from "@bindery/dynamic-keyboard";
import { Check, Mic, Sparkles, UserRound, X } from "lucide-react";
import {
  type ReactNode,
  Suspense,
  use,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  admit,
  type Admission,
  asWritten,
  bestChip,
  deadEnd,
  forget,
  remember,
  type SuggestMemory,
} from "./keyboard-gate";
import { ComposeSheet, ComposeSheetTitle } from "./compose-sheet";
import { requestSuggestion } from "./suggest-client";

export interface ComposeKeyboardProps {
  /** The story before the word being typed, as the suggest route's text. */
  readonly context: string;
  readonly onCommit: (word: string, via: Exclude<Admission, "refused">) => void;
  readonly onSayInstead: () => void;
  readonly onSaveForAdult?: () => void;
  /** Speaks a word or a short cue in the page's voice. */
  readonly onSay: (text: string) => void;
  readonly getToken?: () => Promise<string | null>;
}

function Rescue({
  chip,
  onTake,
  onSay,
  onSayInstead,
  onSaveForAdult,
}: {
  chip: string | null;
  onTake: (word: string) => void;
  onSay: (text: string) => void;
  onSayInstead: () => void;
  onSaveForAdult?: () => void;
}) {
  const [heard, setHeard] = useState(false);
  return (
    <div className="compose-rescue flex flex-col items-center gap-3">
      <p
        className="m-0 text-center text-xl"
        style={{ color: "var(--ink-soft)" }}
      >
        That&rsquo;s a tricky one. Try one of these.
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        {chip === null ? null : (
          <>
            <button
              type="button"
              className="compose-btn"
              data-tone="hear"
              data-picked={heard ? "true" : undefined}
              onClick={() => {
                setHeard(true);
                onSay(chip);
              }}
            >
              <Sparkles className="size-6" /> {chip}
            </button>
            <button
              type="button"
              className="compose-btn"
              data-tone="yes"
              disabled={!heard}
              aria-label={`Use the word ${chip}`}
              onClick={() => onTake(chip)}
            >
              <Check className="size-7" /> That one
            </button>
          </>
        )}
        <button
          type="button"
          className="compose-btn"
          data-tone="talk"
          onClick={onSayInstead}
        >
          <Mic className="size-6" /> Say it instead
        </button>
        {onSaveForAdult === undefined ? null : (
          <button
            type="button"
            className="compose-btn"
            data-tone="grownup"
            onClick={onSaveForAdult}
          >
            <UserRound className="size-6" /> Save it for a grown-up
          </button>
        )}
      </div>
    </div>
  );
}

function KeyboardBody({
  model,
  context,
  onCommit,
  onSayInstead,
  onSaveForAdult,
  onSay,
  getToken = fetchToken,
}: ComposeKeyboardProps & { model: KeyboardModel }) {
  const [memory, setMemory] = useState<SuggestMemory>(forget);
  // The keyboard owns the letters being typed and shows them to the host
  // only by asking about them; the last prefix asked is the host's view of
  // what is on the keys, and what the rescue is keyed by.
  const [typed, setTyped] = useState<string | null>(null);
  const [refused, setRefused] = useState<{
    word: string;
    chip: string | null;
  } | null>(null);

  const suggest: SuggestWord = async (prefix, { signal }) => {
    setTyped(prefix);
    // A refusal stands until the child types something other than the word
    // that was refused; an answer arriving does not lift it.
    setRefused((r) => (r !== null && r.word !== prefix ? null : r));
    const answer = await requestSuggestion({
      request: { text: context, prefix },
      getToken,
      signal,
    });
    // No answer is not "not a word": a rejection lets the keyboard hand the
    // letters over unchecked, where the gate decides, instead of leaving
    // Done dark for the session.
    if (answer === null) throw new Error("no suggestion");
    setMemory((m) => remember(m, prefix, answer));
    return answer;
  };

  // The keyboard clears its own letters only when it finishes a word itself.
  // The rescue commits from outside it, so a rescue gives it a fresh mount,
  // and nothing else does: a remount drops whatever focus the keys held.
  const [rescues, setRescues] = useState(0);
  const body = useRef<HTMLDivElement>(null);
  const refocus = useRef(false);

  // The rescue's buttons leave with the rescue, which would drop focus to
  // the page; it lands on the fresh keyboard instead, and only if it was
  // here to begin with.
  useLayoutEffect(() => {
    if (!refocus.current) return;
    refocus.current = false;
    body.current?.querySelector<HTMLElement>("fieldset")?.focus();
  });

  // The host only forgets what it learned about the word.
  const reset = () => {
    setMemory(forget());
    setTyped(null);
    setRefused(null);
  };

  const take = (word: string, via: Exclude<Admission, "refused">) => {
    reset();
    onCommit(asWritten(word), via);
  };

  const takeRescue = (word: string) => {
    refocus.current = body.current?.contains(document.activeElement) ?? false;
    setRescues((n) => n + 1);
    take(word, "chip");
  };

  const onWordComplete = (word: string) => {
    const via = admit(memory, word);
    if (via === "refused") {
      setRefused({ word, chip: bestChip(memory, word) });
      onSay("Hmm. Let's try another way.");
      return;
    }
    take(word, via);
  };

  const stuck = refused ?? deadEnd(model, memory, typed);

  return (
    <div ref={body} className="flex flex-col gap-4">
      <div className="compose-keyboard">
        <DynamicKeyboard
          key={rescues}
          model={model}
          suggest={suggest}
          context={context}
          onWordComplete={onWordComplete}
          className="max-w-none border-0 bg-transparent shadow-none"
        />
      </div>
      {stuck === null ? (
        <div className="flex justify-center">
          <button
            type="button"
            className="compose-btn"
            data-tone="talk"
            style={{ fontSize: "1.1rem", minHeight: "3.25rem" }}
            onClick={() => {
              reset();
              onSayInstead();
            }}
          >
            <Mic className="size-6" /> Say it instead
          </button>
        </div>
      ) : (
        <Rescue
          key={stuck.chip ?? ""}
          chip={stuck.chip}
          onTake={takeRescue}
          onSay={onSay}
          onSayInstead={() => {
            reset();
            onSayInstead();
          }}
          {...(onSaveForAdult === undefined ? {} : { onSaveForAdult })}
        />
      )}
    </div>
  );
}

function Loading() {
  return (
    <p
      className="m-0 py-10 text-center text-xl"
      style={{ color: "var(--ink-soft)" }}
    >
      Getting the letters ready…
    </p>
  );
}

function Loaded(props: ComposeKeyboardProps) {
  const model = use(loadKeyboardModel());
  return <KeyboardBody {...props} model={model} />;
}

export function ComposeKeyboard(props: ComposeKeyboardProps) {
  return (
    <Suspense fallback={<Loading />}>
      <Loaded {...props} />
    </Suspense>
  );
}

/** The bottom sheet the keyboard lifts in when the child spells a word at the cursor. */
export function KeyboardSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <ComposeSheet className="flex flex-col gap-4" onClose={onClose}>
      <div className="flex items-center justify-between">
        <ComposeSheetTitle
          className="m-0 text-xl font-normal"
          style={{ color: "var(--ink-soft)" }}
        >
          {title}
        </ComposeSheetTitle>
        <button
          type="button"
          className="compose-icon"
          aria-label="Close"
          onClick={onClose}
        >
          <X className="size-7" />
        </button>
      </div>
      {children}
    </ComposeSheet>
  );
}
